import { useEffect, useState } from 'react';

/**
 * Returns whether the given media query currently matches.
 * Subscribes to changes via matchMedia, so the value updates on resize.
 *
 * SSR-safe: returns `false` when `window` is unavailable. The Player page
 * relies on the Tailwind `lg:` breakpoint (1024px) to switch between mobile
 * and desktop layouts, and a few props need the boolean in JS.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** Tailwind `lg:` breakpoint — true below 1024px. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 1023px)');
}
